import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { analytics } from "./analytics";
import { isMobileDevice } from "./device";

export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const needsHomeScreen = () => isIOS() && !isStandalone();
export const platformLabel = () => (isIOS() ? "ios" : /Android/i.test(navigator.userAgent) ? "android" : isMobileDevice() ? "mobile" : "desktop");

const b64ToU8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

let regPromise = null;
export const getRegistration = () => {
  if (!pushSupported()) return Promise.resolve(null);
  regPromise = regPromise || navigator.serviceWorker.register("/sw.js").catch(() => null);
  return regPromise;
};

export const getSubscription = async () => {
  const reg = await getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
};

const emit = () => window.dispatchEvent(new Event("tc:push-changed"));

const register = async (sub) => {
  const { data } = await api.post("/push/subscribe", {
    ...sub.toJSON(), user_agent: navigator.userAgent, platform: platformLabel(), standalone: isStandalone(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  });
  return data;
};

const withTimeout = (p, ms, label) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error(label)), ms))]);

export const subscribePush = async () => {
  if (!pushSupported()) throw new Error("unsupported");
  if (needsHomeScreen()) throw new Error("homescreen");
  const perm = await withTimeout(Notification.requestPermission(), 60000, "timeout");
  window.dispatchEvent(new Event("tc:notif-permission"));
  if (perm !== "granted") throw new Error(perm);
  const reg = await withTimeout(getRegistration().then((r) => r && navigator.serviceWorker.ready.then(() => r)), 15000, "timeout");
  if (!reg) throw new Error("unsupported");
  const { data: cfg } = await api.get("/push/vapid-public-key");
  if (!cfg.enabled) throw new Error("disabled");
  const sub = (await reg.pushManager.getSubscription()) || (await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(cfg.public_key) }), 15000, "timeout"));
  const out = await register(sub);
  analytics.track("push_enabled", { category: "notifications", label: platformLabel(), metadata: { devices: out.devices, standalone: isStandalone() } });
  emit();
  return out;
};

export const unsubscribePush = async () => {
  const sub = await getSubscription();
  if (sub) {
    try { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); } catch { /* server record may be gone */ }
    await sub.unsubscribe();
  }
  analytics.track("push_disabled", { category: "notifications", label: platformLabel() });
  emit();
};

export const forgetPushOnLogout = async () => {
  try {
    const sub = await getSubscription();
    if (sub) await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } });
  } catch { /* ignore */ }
};

// Keep the server copy fresh (new login on the same device, rotated endpoint, timezone changes).
export const syncPush = async () => {
  try {
    const sub = await getSubscription();
    if (sub && Notification.permission === "granted") await register(sub);
  } catch { /* offline or not configured */ }
};

export const sendTestPush = () => api.post("/push/test").then((r) => r.data);

export const trackPushOpen = () => {
  const clean = consumePushUrl(`${window.location.pathname}${window.location.search}`);
  if (clean !== `${window.location.pathname}${window.location.search}`) window.history.replaceState({}, "", clean);
};

// Strips push tracking params from a URL; records push_opened when they were present.
export const consumePushUrl = (url) => {
  const u = new URL(url, window.location.origin);
  if (u.searchParams.get("via") !== "push") return `${u.pathname}${u.search}`;
  analytics.track("push_opened", { category: "notifications", label: u.searchParams.get("reason") || u.searchParams.get("kind") || "push",
    metadata: { kind: u.searchParams.get("kind"), standalone: isStandalone() } });
  ["via", "kind", "reason"].forEach((k) => u.searchParams.delete(k));
  return `${u.pathname}${u.search}`;
};

export const onServiceWorkerMessage = (handler) => {
  if (!pushSupported()) return () => {};
  const h = (e) => handler(e.data || {});
  navigator.serviceWorker.addEventListener("message", h);
  return () => navigator.serviceWorker.removeEventListener("message", h);
};

export const usePushStatus = () => {
  const [state, setState] = useState({ supported: pushSupported(), permission: pushSupported() ? Notification.permission : "unsupported", subscribed: false, devices: null, enabled: null, loading: true });
  const refresh = useCallback(async () => {
    if (!pushSupported()) { setState((s) => ({ ...s, loading: false })); return; }
    const sub = await getSubscription();
    let devices = null, enabled = null;
    try { const { data } = await api.get("/push/subscriptions"); devices = data.subscriptions; enabled = data.enabled; } catch { /* ignore */ }
    setState({ supported: true, permission: Notification.permission, subscribed: !!sub && !!devices?.some((d) => d.endpoint === sub.endpoint), devices, enabled, loading: false });
  }, []);
  useEffect(() => {
    refresh();
    window.addEventListener("tc:push-changed", refresh);
    window.addEventListener("tc:notif-permission", refresh);
    return () => { window.removeEventListener("tc:push-changed", refresh); window.removeEventListener("tc:notif-permission", refresh); };
  }, [refresh]);
  return { ...state, refresh };
};
